import {
  Flex,
  Icon,
  Input,
  StyleProps,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import { HiCheckCircle } from "react-icons/hi2";

import {
  Button,
  ConfirmModal,
  errorToast,
  Link,
  successToast,
  useToast,
} from "../../../components";
import { copy } from "../../../utils/clipboard";
import { formatDate } from "../../../utils/datetime";
import {
  AshbyAssessmentApiTokensDocument,
  useAshbyAssessmentApiTokensQuery,
  useCreateAshbyAssessmentApiTokenMutation,
  useSaveAshbyAssessmentApiTokenMutation,
} from "../../graphql";

type AshbyAssessmentApiKeySettingsProps = StyleProps & {
  //
};

export const AshbyAssessmentApiKeySettings: React.FC<
  AshbyAssessmentApiKeySettingsProps
> = ({ ...styles }) => {
  const toast = useToast();
  const ashbyTokenInputRef = useRef<HTMLInputElement>(null);
  const [isAddingAshbyToken, setIsAddingAshbyToken] = useState(false);

  const { data: tokensData, loading: tokensLoading } =
    useAshbyAssessmentApiTokensQuery({
      onCompleted: (data) => {
        if (!data.ashbyAssessmentApiTokens?.ashbyToken) {
          setIsAddingAshbyToken(true);
        }
      },
      onError: () => {
        errorToast(toast, "There was a problem fetching API keys");
      },
    });
  const [createToken, { data: createTokenData, loading: createTokenLoading }] =
    useCreateAshbyAssessmentApiTokenMutation({
      onCompleted: (data) => {
        if (data.createAshbyAssessmentApiToken?.token) {
          successToast(toast, "API key created");
        }
      },
      onError: () => {
        errorToast(toast, "There was a problem creating the API key");
      },
    });
  const [saveToken, { loading: saveTokenLoading }] =
    useSaveAshbyAssessmentApiTokenMutation({
      onCompleted: (data) => {
        if (data.saveAshbyAssessmentApiToken?.ashbyToken) {
          successToast(toast, "API key saved");
          setIsAddingAshbyToken(false);
        }
      },
      onError: () => {
        errorToast(toast, "There was a problem saving the API key");
      },
      update: (cache, { data }) => {
        const ashbyToken = data?.saveAshbyAssessmentApiToken?.ashbyToken;
        if (ashbyToken) {
          cache.writeQuery({
            query: AshbyAssessmentApiTokensDocument,
            data: {
              ashbyAssessmentApiTokens: {
                ...tokensData?.ashbyAssessmentApiTokens,
                ashbyToken,
              },
            },
          });
        }
      },
    });

  const confirmModal = useDisclosure();

  const tokens = tokensData?.ashbyAssessmentApiTokens;
  const bhTokenPrefix = tokens?.bhToken?.tokenPrefix
    ? `${tokens.bhToken.tokenPrefix}......`
    : "";
  const bhTokenCreatedAt = tokens?.bhToken?.createdAt;
  const hasBhToken = !!bhTokenPrefix;
  const newBhToken =
    createTokenData?.createAshbyAssessmentApiToken?.token ?? "";

  const ashbyTokenPrefix = tokens?.ashbyToken?.tokenPrefix;
  const ashbyTokenCreatedAt = tokens?.ashbyToken?.createdAt;
  const hasAshbyToken = !!ashbyTokenPrefix;
  const showSavedAshbyToken = ashbyTokenPrefix && !isAddingAshbyToken;

  if (tokensLoading) {
    return null;
  }

  const copyToken = (): void => {
    copy(newBhToken);
    toast.closeAll();
    successToast(toast, "API key copied to clipboard");
  };

  return (
    <Flex direction="column" gap={2} {...styles}>
      <Text fontWeight="semibold">
        AI Interviewer API keys{" "}
        {hasBhToken && hasAshbyToken && (
          <Icon
            as={HiCheckCircle}
            color="green.500"
            boxSize="5"
            pos="relative"
            top="1"
          />
        )}
      </Text>
      <Text>
        {newBhToken ? (
          <>
            Copy this API key and paste it into{" "}
            <Link
              href="https://app.ashbyhq.com/admin/integrations/marketplace/brighthire-screen"
              target="_blank"
            >
              Ashby
            </Link>{" "}
            under &quot;BrightHire Screen API Key&quot;. You will not be able to
            access this key again after you leave this page.
          </>
        ) : bhTokenCreatedAt ? (
          `This BrightHire API key was created on ${formatDate(
            bhTokenCreatedAt
          )}.`
        ) : (
          "Create a BrightHire API key and save it in Ashby to enable the AI Interviewer."
        )}
      </Text>
      <Flex gap="2">
        <Input
          isReadOnly
          placeholder="No BrightHire API key generated"
          value={newBhToken || bhTokenPrefix}
          onClick={(e) => {
            if (newBhToken) {
              e.currentTarget.select();
            }
          }}
          flex="1"
        />
        {newBhToken ? (
          <Button onClick={copyToken}>Copy</Button>
        ) : (
          <Button
            onClick={hasBhToken ? confirmModal.onOpen : () => createToken()}
            isLoading={createTokenLoading}
          >
            {hasBhToken ? "Regenerate" : "Generate"}
          </Button>
        )}
      </Flex>
      <Text mt="3">
        {showSavedAshbyToken
          ? `This Ashby API key was saved to BrightHire on ${formatDate(
              ashbyTokenCreatedAt
            )}.`
          : "Create an API key in Ashby and save it here to enable the AI Interviewer."}
      </Text>
      <Flex gap="2">
        {showSavedAshbyToken ? (
          <>
            <Input
              isReadOnly
              name="readonly-ashby-token"
              key="readonly-ashby-token"
              value={ashbyTokenPrefix}
              flex="1"
            />
            <Button onClick={() => setIsAddingAshbyToken(true)}>Add new</Button>
          </>
        ) : (
          <>
            <Input
              ref={ashbyTokenInputRef}
              name="ashby-token"
              key="ashby-token"
              placeholder="No Ashby API key saved"
              flex="1"
            />
            {hasAshbyToken && (
              <Button
                variant="outline"
                onClick={() => setIsAddingAshbyToken(false)}
              >
                Cancel
              </Button>
            )}
            <Button
              isLoading={saveTokenLoading}
              onClick={() => {
                const token = ashbyTokenInputRef.current?.value?.trim();
                if (token) {
                  saveToken({ variables: { apiKey: token } });
                } else {
                  errorToast(toast, "Please enter a valid API key");
                  ashbyTokenInputRef.current?.focus();
                }
              }}
            >
              Save
            </Button>
          </>
        )}
      </Flex>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onCancel={confirmModal.onClose}
        onConfirm={() => {
          confirmModal.onClose();
          createToken();
        }}
        modalTitleText="Regenerate API key"
        modalBodyText="Are you sure you want to generate a new API key? This will invalidate your existing API key."
        modalSubmitText="Regenerate"
      />
    </Flex>
  );
};
